import {
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  Link,
  TextField,
  Typography,
} from "@mui/material";
import { Mail } from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";

import AuthFormHeader from "../components/AuthFormHeader";
import AuthShell from "../components/AuthShell";
import PasswordField from "../components/PasswordField";
import VerificationCodeField from "../components/VerificationCodeField";
import { authCopy } from "../config/authCopy";
import { authRoutes } from "../config/authRoutes";
import { useLoginForm } from "../hooks/useLoginForm";

export default function LoginPage() {
  const {
    checkingSession,
    confirmPassword,
    confirmPasswordError,
    confirmPasswordHelperText,
    handleSubmit,
    isMfa,
    isNewPassword,
    loading,
    message,
    mfaCode,
    mfaError,
    mfaHelperText,
    newPassword,
    newPasswordError,
    newPasswordHelperText,
    password,
    passwordError,
    passwordHelperText,
    setConfirmPassword,
    setMfaCode,
    setNewPassword,
    setPassword,
    setUsername,
    username,
    usernameError,
    usernameHelperText,
  } = useLoginForm();

  if (checkingSession) {
    return (
      <Box
        sx={(theme) => ({
          alignItems: "center",
          bgcolor: theme.palette.surface.default,
          display: "flex",
          justifyContent: "center",
          minHeight: "100vh",
        })}
      >
        <CircularProgress />
      </Box>
    );
  }

  const copy = isMfa
    ? authCopy.loginMfa
    : isNewPassword
      ? {
          ...authCopy.login,
          title: "Defina sua senha",
          description: "Crie uma senha definitiva para acessar sua conta.",
        }
      : authCopy.login;
  const statusMessage = !isMfa && !isNewPassword && message && !usernameError && !passwordError
    ? message
    : undefined;

  return (
    <AuthShell
      brandTitle={copy.brandTitle}
      brandDescription={copy.brandDescription}
      showIntegrations={!isMfa}
    >
      <AuthFormHeader title={copy.title} description={copy.description} />

      <Box component="form" onSubmit={handleSubmit} noValidate>
        {isMfa ? (
          <LoginMfaForm
            code={mfaCode}
            error={mfaError}
            helperText={mfaHelperText}
            loading={loading}
            onCodeChange={setMfaCode}
          />
        ) : isNewPassword ? (
          <LoginNewPasswordForm
            confirmPassword={confirmPassword}
            confirmPasswordError={confirmPasswordError}
            confirmPasswordHelperText={confirmPasswordHelperText}
            loading={loading}
            newPassword={newPassword}
            newPasswordError={newPasswordError}
            newPasswordHelperText={newPasswordHelperText}
            setConfirmPassword={setConfirmPassword}
            setNewPassword={setNewPassword}
          />
        ) : (
          <LoginCredentialsForm
            loading={loading}
            password={password}
            passwordError={passwordError}
            passwordHelperText={passwordHelperText}
            setPassword={setPassword}
            setUsername={setUsername}
            username={username}
            usernameError={usernameError}
            usernameHelperText={usernameHelperText || statusMessage}
          />
        )}
      </Box>
    </AuthShell>
  );
}

function LoginCredentialsForm({
  loading,
  password,
  passwordError,
  passwordHelperText,
  setPassword,
  setUsername,
  username,
  usernameError,
  usernameHelperText,
}) {
  return (
    <>
      <LoginIdentifierField
        value={username}
        onChange={(event) => setUsername(event.target.value)}
        error={usernameError}
        helperText={usernameHelperText}
      />

      <PasswordField
        label="Senha"
        required
        name="password"
        value={password}
        onChange={(event) => setPassword(event.target.value)}
        error={passwordError}
        helperText={passwordHelperText}
      />

      <Box sx={{ display: "flex", justifyContent: "center", mb: 2, mt: 0.5 }}>
        <Link
          component={RouterLink}
          to={authRoutes.forgotPassword}
          variant="caption"
          sx={{ fontWeight: 600, textAlign: "center" }}
        >
          Esqueci minha senha
        </Link>
      </Box>

      <Button
        type="submit"
        variant="contained"
        fullWidth
        disabled={loading}
      >
        {loading ? <CircularProgress color="inherit" size={18} /> : "Entrar"}
      </Button>

      <Typography
        sx={(theme) => ({
          color: theme.palette.text.primary,
          fontSize: theme.typography.body2.fontSize,
          mt: { xs: 4, md: 5.5 },
          textAlign: "center",
          "@media (max-height: 620px)": {
            mt: 2.5,
          },
        })}
      >
        É novo por aqui?
      </Typography>

      <Box sx={{ display: "flex", justifyContent: "center", mt: 0.5 }}>
        <Link
          component={RouterLink}
          to={authRoutes.signup}
          variant="caption"
          sx={{ fontWeight: 600, textAlign: "center" }}
        >
          Crie sua conta
        </Link>
      </Box>
    </>
  );
}

function LoginNewPasswordForm({
  confirmPassword,
  confirmPasswordError,
  confirmPasswordHelperText,
  loading,
  newPassword,
  newPasswordError,
  newPasswordHelperText,
  setConfirmPassword,
  setNewPassword,
}) {
  return (
    <>
      <PasswordField
        label="Nova senha"
        required
        name="newPassword"
        value={newPassword}
        onChange={(event) => setNewPassword(event.target.value)}
        error={newPasswordError}
        helperText={newPasswordHelperText}
      />

      <PasswordField
        label="Confirmar senha"
        required
        name="confirmPassword"
        value={confirmPassword}
        onChange={(event) => setConfirmPassword(event.target.value)}
        error={confirmPasswordError}
        helperText={confirmPasswordHelperText}
      />

      <Button
        type="submit"
        variant="contained"
        fullWidth
        disabled={loading}
      >
        {loading ? <CircularProgress color="inherit" size={18} /> : "Salvar senha"}
      </Button>
    </>
  );
}

function LoginMfaForm({
  code,
  error,
  helperText,
  loading,
  onCodeChange,
}) {
  return (
    <>
      <VerificationCodeField
        label="Código para autenticação"
        required
        name="mfaCode"
        value={code}
        onChange={onCodeChange}
        autoFocus
        error={error}
        helperText={helperText}
      />

      <Button
        type="submit"
        variant="contained"
        fullWidth
        disabled={loading}
      >
        {loading ? <CircularProgress color="inherit" size={18} /> : "Confirmar"}
      </Button>
    </>
  );
}

function LoginIdentifierField({
  value,
  onChange,
  error,
  helperText,
}) {
  return (
    <Box sx={{ mb: helperText ? 1 : 1.5 }}>
      <Typography
        component="label"
        htmlFor="auth-username"
        sx={(theme) => ({
          color: theme.palette.text.primary,
          display: "block",
          fontSize: theme.typography.body2.fontSize,
          fontWeight: 500,
          lineHeight: "20px",
          mb: 0.75,
        })}
      >
        Email *
      </Typography>

      <TextField
        id="auth-username"
        name="username"
        value={value}
        onChange={onChange}
        autoComplete="username"
        error={error}
        helperText={helperText}
        fullWidth
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Mail fontSize="small" />
            </InputAdornment>
          ),
        }}
      />
    </Box>
  );
}
